import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../../config.json';
// Shares the Features page styles (colours, type, buttons) so both pages match.
import '../features/features.css';
import './ContactForm.css';

const API_ENDPOINT = `${config.REACT_APP_API_URL}/wayapay-notification-api/email-notification-contact`;
const TEAM_EMAIL = 'info@wayaquick.com';
const PHONE_DISPLAY = '+234 915 4978 790';
const PHONE_LINK = 'tel:+2349154978790';
const ADDRESS = '14 Yeye Olofin Street, Lekki Phase 1, Lagos, Nigeria';
const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;

const EMPTY = { fullname: '', email: '', phone: '', subject: '', description: '' };

const buildPayload = (email, fullName, message) => ({
    data: { message, names: [{ email, fullName }] },
    eventCategory: 'CONTACT',
    eventType: 'EMAIL',
    initiator: '1',
    productType: 'WAYAPAY',
});

const errorText = (err) => {
    const msg = err?.response?.data?.message;
    return typeof msg === 'string' && msg.trim()
        ? msg
        : `We couldn't send your message. Please try again or email ${TEAM_EMAIL}.`;
};

function ContactForm() {
    const [form, setForm] = useState(EMPTY);
    // idle | sending | sent | error
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');

    const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (status === 'sending') return;

        const fields = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
        if (Object.values(fields).some((v) => !v)) {
            setStatus('error');
            setError('Please fill in every field.');
            return;
        }

        // The team's copy carries the sender's contact details and subject,
        // so the team can reply. The previous form sent only the message text.
        const teamMessage = [
            `Subject: ${fields.subject}`,
            `From: ${fields.fullname} (${fields.email})`,
            `Phone: ${fields.phone}`,
            '',
            fields.description,
        ].join('\n');

        setStatus('sending');
        setError('');
        try {
            await axios.post(API_ENDPOINT, buildPayload(TEAM_EMAIL, fields.fullname, teamMessage));
        } catch (err) {
            setStatus('error');
            setError(errorText(err));
            return;
        }

        // The message has reached the team. The confirmation email to the sender
        // is a courtesy: if it fails, the send still counts as successful.
        axios
            .post(
                API_ENDPOINT,
                buildPayload(fields.email, fields.fullname, 'Your message has been delivered and will be attended to.')
            )
            .catch(() => { });

        setForm(EMPTY);
        setStatus('sent');
    };

    const sending = status === 'sending';

    return (
        <main className="wqf wqc">
            <div className="wqf-wrap wqc-grid">
                <section className="wqc-intro" aria-labelledby="wqc-title">
                    <h1 id="wqc-title" className="wqf-h1">
                        Talk to the WayaQuick team
                    </h1>
                    <p className="wqf-lead">
                        Questions about payments, pricing or adding WayaQuick to your business? Send us a message,
                        or reach us directly.
                    </p>

                    <dl className="wqc-details">
                        <div>
                            <dt>Email</dt>
                            <dd>
                                <a href={`mailto:${TEAM_EMAIL}`}>{TEAM_EMAIL}</a>
                            </dd>
                        </div>
                        <div>
                            <dt>Phone</dt>
                            <dd>
                                <a href={PHONE_LINK}>{PHONE_DISPLAY}</a>
                            </dd>
                        </div>
                        <div>
                            <dt>Office</dt>
                            <dd>
                                {ADDRESS}
                                <a className="wqc-sub-link" href={MAPS_LINK} target="_blank" rel="noopener noreferrer">
                                    Get directions
                                </a>
                            </dd>
                        </div>
                    </dl>

                    <h2 className="wqf-h4 wqc-help-title">Other ways to get help</h2>
                    <ul className="wqc-help">
                        <li>
                            <Link to="/get-started">Read the documentation</Link>
                            <span className="wqf-small">Guides for getting started, payment links and integrations.</span>
                        </li>
                        <li>
                            <Link to="/pricing">See pricing</Link>
                            <span className="wqf-small">What it costs to accept payments with WayaQuick.</span>
                        </li>
                    </ul>
                </section>

                <section className="wqc-card" aria-labelledby="wqc-form-title">
                    <h2 id="wqc-form-title" className="wqf-h3">
                        Send us a message
                    </h2>

                    {status === 'sent' ? (
                        <div className="wqc-sent" role="status">
                            <p className="wqf-h4">Message sent</p>
                            <p className="wqf-small">
                                Thanks for getting in touch. We've emailed you a confirmation and the team will reply
                                to the email address you gave us.
                            </p>
                            <button type="button" className="wqf-btn wqf-btn-ghost" onClick={() => setStatus('idle')}>
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="wqc-row">
                                <div className="wqc-field">
                                    <label htmlFor="wqc-name">Full name</label>
                                    <input
                                        id="wqc-name"
                                        name="fullname"
                                        type="text"
                                        autoComplete="name"
                                        required
                                        value={form.fullname}
                                        onChange={update}
                                    />
                                </div>
                                <div className="wqc-field">
                                    <label htmlFor="wqc-phone">Phone number</label>
                                    <input
                                        id="wqc-phone"
                                        name="phone"
                                        type="tel"
                                        autoComplete="tel"
                                        inputMode="tel"
                                        required
                                        value={form.phone}
                                        onChange={update}
                                    />
                                </div>
                            </div>
                            <div className="wqc-field">
                                <label htmlFor="wqc-email">Email address</label>
                                <input
                                    id="wqc-email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    value={form.email}
                                    onChange={update}
                                />
                            </div>
                            <div className="wqc-field">
                                <label htmlFor="wqc-subject">Subject</label>
                                <input
                                    id="wqc-subject"
                                    name="subject"
                                    type="text"
                                    required
                                    value={form.subject}
                                    onChange={update}
                                />
                            </div>
                            <div className="wqc-field">
                                <label htmlFor="wqc-message">Message</label>
                                <textarea
                                    id="wqc-message"
                                    name="description"
                                    rows={6}
                                    required
                                    value={form.description}
                                    onChange={update}
                                />
                            </div>

                            {status === 'error' && (
                                <p className="wqc-error" role="alert">
                                    {error}
                                </p>
                            )}

                            <button type="submit" className="wqf-btn wqf-btn-primary wqc-submit" disabled={sending}>
                                {sending ? 'Sending…' : 'Send message'}
                            </button>
                        </form>
                    )}
                </section>
            </div>
        </main>
    );
}

export default ContactForm;

